import { IConfigSeller } from '@/models/configSeller'
import { webApiService } from '@/services'
import { UTCDateToCountryDate } from '@/utils/countryDate.util'
import { StatusColor } from '@/utils/StatusColor.utility'
import { Button as LegacyButton, Pagination, SortDescriptor, Selection } from '@nextui-org/react'
import { Button } from '@/components/ui/button'
import { hasRedesign } from '@/utils/redesignSellers'
import { enqueueSnackbar } from 'notistack'
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import XLSX from 'xlsx-js-style'

const useTableDataDinamic = (columns: any[], excelName: string, tableType: string, listStatus: number[], region?: string, store?: string) => {
    type LoadDataParams = {
    pageProps: number,
    rowsPerPageProps: number,
    searchProps: string
    listStatusProps?: number[]
  }

    const [data, setData] = useState<{ loading: boolean, loadingExcel: boolean, list: any[], count: number }>({ loading: false, loadingExcel: false, list: [], count: 0 })
    const [page, setPage] = useState(1)
    const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
      column: 'age',
      direction: 'ascending'
    })
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [filterValue, setFilterValue] = useState('')
    const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]))
    const filterValueRef = useRef<string>(filterValue)
    const listStatusDinamicRef = useRef(listStatus)
    const sealRef = useRef<string>('none')
    const regionRef = useRef<string | undefined>(region)
    const storeRef = useRef<string | undefined>(store)
    const isFirstRegionRender = useRef(true)
    const isSamsungMexico = configSeller.config.s3.FOLDER_APP === 'ti_samsung_mx'
    const isFalabellaChile = configSeller.config.s3.FOLDER_APP === 'ti_falabella'
    const isCoppelMexico = configSeller.config.s3.FOLDER_APP === 'ti_coppel'
    const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
    const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'

    const hasSearchFilter = Boolean(filterValue)

    if (configSeller.config.permisos.ENABLE_CONTRIBUTION !== 1) {
      columns = columns.filter((col: any) => col.uid !== 'contribution_amount')
    }

    if (configSeller.config.s3.FOLDER_APP === 'ti_samsung_pe_rom') {
      columns.push({ name: 'DNI', uid: 'rut' })
    }

    useEffect(() => {
      filterValueRef.current = filterValue
    }, [filterValue])

    const pages = Math.ceil(data.count / rowsPerPage)

    const items = data.list

    const sortedItems = useMemo(() => {
      return data.list
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortDescriptor, items, data])

    /* --------------------------------------------------------- */
    const { slug } = useParams()

    const userState = useSelector((store: any) => store.user)

    // Sucursal que el usuario eligió en el modal de ingreso (sesión SSO de WOM). Va en un
    // ref para que loadData la lea siempre actualizada sin depender del render.
    const userStoreId: number | null = userState.selectedStore ?? null
    const userStoreRef = useRef<number | null>(userStoreId)
    const isFirstUserStoreRender = useRef(true)

    const loadData = async ({ pageProps, rowsPerPageProps, searchProps, listStatusProps }: LoadDataParams) => {
      setData({ ...data, loading: true })
      const currentListStatus = listStatusDinamicRef.current
      await webApiService.getListTradeIn({
        email: userState.email,
        poolId: String(import.meta.env.VITE_COGNITO_POOL_ID),
        listStatus: listStatusProps || currentListStatus,
        id_role: userState.id_role,
        seller: slug,
        page: pageProps || 1,
        limit: rowsPerPageProps,
        searchType: tableType,
        search: searchProps,
        downloadExcel: false,
        seal: sealRef.current,
        // Solo para Coppel ( primer seller en manejar regiones ) vamos a mandar la region y si viene, la tienda
        region: isCoppelMexico ? (regionRef.current || null) : null,
        // En Coppel la tienda viene del filtro de la tabla; en el resto, de la sucursal
        // que el usuario eligió al ingresar (solo la tiene la sesión SSO de WOM).
        selectedStore: isCoppelMexico ? (storeRef.current || null) : (userStoreRef.current || null)
      }).then((response) => {
        // Si el servicio no devuelve el listado (respuesta de error o sin data) dejamos la tabla vacía:
        // `list` siempre tiene que ser un arreglo porque el resto del hook lo recorre sin validar.
        const list = Array.isArray(response?.data?.info) ? response.data.info : []
        setData({ loading: false, list, count: Number(response?.data?.count ?? 0), loadingExcel: false })
      }).catch((error) => {
        console.log('error getListTradeIn => ', error)
        setData({ loading: false, list: [], count: 0, loadingExcel: false })
        enqueueSnackbar('No se pudo cargar el listado', { variant: 'error' })
      })
    }

    useEffect(() => {
      loadData({ pageProps: 1, rowsPerPageProps: 10, searchProps: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      regionRef.current = region
      storeRef.current = store
      // Evitamos recargar en el primer render (la carga inicial ya la hace el efecto de arriba)
      if (isFirstRegionRender.current) {
        isFirstRegionRender.current = false
        return
      }
      setPage(1)
      loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: filterValueRef.current })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [region, store])

    useEffect(() => {
      userStoreRef.current = userStoreId
      // La carga inicial ya sale con la sucursal que traía la sesión; acá solo recargamos
      // cuando el usuario la elige (o la cambia) en el modal de ingreso.
      if (isFirstUserStoreRender.current) {
        isFirstUserStoreRender.current = false
        return
      }
      setPage(1)
      loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: filterValueRef.current })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userStoreId])

    /* --------------------------------------------------------- */
    const onNextPage = useCallback(() => {
      if (page < pages) {
        setPage(page + 1)
        loadData({ pageProps: page + 1, rowsPerPageProps: rowsPerPage, searchProps: filterValue })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pages])

    const onPreviousPage = useCallback(() => {
      if (page > 1) {
        setPage(page - 1)
        loadData({ pageProps: page - 1, rowsPerPageProps: rowsPerPage, searchProps: filterValue })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    const onRowsPerPageChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
      setRowsPerPage(Number(e.target.value))
      loadData({ pageProps: 1, rowsPerPageProps: Number(e.target.value), searchProps: filterValueRef.current })
      setPage(1)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const onSearchChange = (value: any) => {
      setFilterValue(value)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setPage(1)
        loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: value })
      }, 500)
    }

    const onChangeTab = useCallback((value: any) => {
      console.log('🚀 ~ onChangeTab ~ value:', value)
      listStatusDinamicRef.current = value === 'recibidos' ? [5] : value === 'retirado' ? [7] : [9]
      if (value === 'custodia') sealRef.current = 'false'
      else if (value === 'sello') sealRef.current = 'true'
      else sealRef.current = 'none'
      loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: filterValueRef.current, listStatusProps: value === 'recibidos' ? [5] : value === 'retirado' ? [7] : [9] })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onClear = useCallback(() => {
      setFilterValue('')
      setPage(1)
      loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: '' })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setStatusFilter = useCallback((listStatusProps: number[]) => {
      listStatusDinamicRef.current = listStatusProps
      setPage(1)
      loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: filterValueRef.current, listStatusProps })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const reload = () => {
      loadData({ pageProps: page, rowsPerPageProps: rowsPerPage, searchProps: filterValueRef.current })
    }

    const handleChangePage = (page: number) => {
      setPage(page)
      loadData({ pageProps: page, rowsPerPageProps: rowsPerPage, searchProps: filterValue })
    }

    // Los sellers sin rediseño (ver REDESIGN_SELLERS) siguen con el paginador anterior.
    const redesign = hasRedesign(configSeller?.config?.s3?.FOLDER_APP)

    const bottomContent = useMemo(() => {
      if (!redesign) {
        return (
          <div className='py-2 px-2 flex justify-between items-center'>
            {
              data.loading
                ? <div>loading ...</div>
                : <Pagination
                    isCompact
                    showControls
                    showShadow
                    color='primary'
                    page={page}
                    total={pages}
                    onChange={handleChangePage}
                  />
            }
            <div className='hidden sm:flex w-[30%] justify-end gap-2'>
              <LegacyButton isDisabled={pages === 1} size='sm' variant='flat' onPress={onPreviousPage}>
                Previous
              </LegacyButton>
              <LegacyButton isDisabled={pages === 1} size='sm' variant='flat' onPress={onNextPage}>
                Next
              </LegacyButton>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-default-400 text-small mr-3'>Total: {data.count} registros</span>
              <label className='flex items-center text-default-400 text-small'>
                Filas por página:
                <select
                  className='bg-transparent outline-none text-default-400 text-small'
                  onChange={onRowsPerPageChange}
                  value={rowsPerPage}
                >
                  <option value='10'>10</option>
                  <option value='25'>25</option>
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                </select>
              </label>
            </div>
          </div>
        )
      }
      return (
        <div className='py-2 px-2 flex flex-wrap justify-between items-center gap-3'>
          <div className='flex items-center gap-2'>
            <Button variant='outline' size='sm' disabled={data.loading || page <= 1} onClick={onPreviousPage}>
              Anterior
            </Button>
            <span className='text-sm text-muted-foreground px-1'>Página {page} de {pages || 1}</span>
            <Button variant='outline' size='sm' disabled={data.loading || page >= pages} onClick={onNextPage}>
              Siguiente
            </Button>
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-muted-foreground text-sm'>Total: {data.count} registros</span>
            <label className='flex items-center gap-1.5 text-muted-foreground text-sm'>
              Filas por página:
              <select
                className='bg-transparent outline-none text-sm text-muted-foreground border border-input rounded-md h-8 px-1.5'
                onChange={onRowsPerPageChange}
                value={rowsPerPage}
              >
                <option value='10'>10</option>
                <option value='25'>25</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </select>
            </label>
          </div>
        </div>
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length, page, pages, hasSearchFilter, data.loading, redesign])

    const exportToCSV = async (type: string, customStartDate?: string, customEndDate?: string) => {
      setData({ ...data, loadingExcel: true })
      const isPrincipal = excelName.toLowerCase().includes('principal')
      if (isPrincipal && !columns.some((ele: any) => ele.uid === 'last_status_date')) {
        columns.push({ name: 'Fecha último estado', uid: 'last_status_date' })
        columns.push({ name: 'Fecha entrega', uid: 'fecha_entregado' })
        columns.push({ name: 'Fecha custodia', uid: 'fecha_custodia' })
        columns.push({ name: 'Fecha Retirado Reuse', uid: 'fecha_retirado' })
        columns.push({ name: 'Usuario que cotizo', uid: 'executive_name' })
        columns.push({ name: 'Usuario que recibio', uid: 'recipient_name' })
        if (configSeller.config.s3.FOLDER_APP === 'ti_coppel') {
          const storeIndex = columns.findIndex((col: any) => col.uid === 'store')
          if (storeIndex !== -1) {
            columns.splice(storeIndex + 1, 0,
              { name: 'Número de tienda', uid: 'seller_store_number' }
            )
          }
          columns.push({ name: 'Folio Coppel', uid: 'folio_coppel' })
          columns.push({ name: 'Fecha Folio', uid: 'fecha_folio_coppel' })
          columns.push({ name: 'Fecha Cotizado', uid: 'fecha_cotizado' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_macstore') {
          columns.push({ name: 'SKU', uid: 'final_sku' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_wom') {
          const emailIndex = columns.findIndex((col: any) => col.uid === 'email')
          if (emailIndex !== -1) {
            columns.splice(emailIndex + 1, 0,
              { name: 'RUT', uid: 'rut' },
              { name: 'Documento', uid: 'document_cupon_used' }
            )
          }
          columns.push(
            { name: 'Dispositivo Comprado', uid: 'device_purchased' },
            { name: 'Sku Dispositivo Comprado', uid: 'sku_purchased' },
            { name: 'Plan Dispositivo Comprado', uid: 'plan_purchased' },
            { name: 'Extra Bono', uid: 'boost_purchased' }
          )
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_huawei_mx') {
          columns.push({ name: 'CURP cliente', uid: 'rut' })
          columns.push({ name: 'Banco', uid: 'bank' })
          columns.push({ name: 'Número de cuenta', uid: 'bank_account' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_samsung_mx') {
          columns.push({ name: 'Serie', uid: 'serie' })
          columns.push({ name: 'Boost', uid: 'boost' })
          columns.push({ name: 'Valor Residual', uid: 'valor_residual' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl' || configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx' || configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe') {
          columns.push({ name: 'Banco', uid: 'bank' })
          columns.push({ name: 'Número de cuenta', uid: 'bank_account' })
          columns.push({ name: 'Tipo de cuenta bancaria', uid: 'bank_account_type' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_falabella') {
          const indexFechaEntregado = columns.findIndex((col: any) => col.uid === 'fecha_entregado')
          if (indexFechaEntregado !== -1) {
            columns.splice(indexFechaEntregado, 0,
              { name: 'Fecha cotizado', uid: 'fecha_cotizado', width: 20 },
              { name: 'Fecha Valiado', uid: 'fecha_validado', width: 20 },
              { name: 'Fecha Firmado', uid: 'fecha_firmado', width: 20 },
              { name: 'RUT cliente', uid: 'rut', width: 20 }
            )
          }
          const indexFechaRetirado = columns.findIndex((col: any) => col.uid === 'fecha_retirado')
          if (indexFechaRetirado !== -1) {
            columns.splice(indexFechaRetirado + 1, 0, { name: 'Fecha Cancelado', uid: 'fecha_cancelado', width: 20 })
          }
          columns.push({ name: 'ID Vendedor Cotizó', uid: 'executive_id' })
          columns.push({ name: 'ID Vendedor Recibió', uid: 'recipient_id' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_paris') {
          columns.push({ name: 'RUT cotizador', uid: 'executive_rut' })
          columns.push({ name: 'RUT receptor', uid: 'recipient_rut' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_ripley_cl') {
          columns.push({ name: 'RUT cotizador', uid: 'executive_rut' })
          columns.push({ name: 'RUT receptor', uid: 'recipient_rut' })
        }
      }
      const currentListStatus = listStatusDinamicRef.current

      let startDate = ''
      let endDate = ''
      const currentDate = new Date()
      if (type === 'last_week') {
        const lastWeek = new Date()
        lastWeek.setDate(currentDate.getDate() - 7)
        startDate = lastWeek.toISOString().split('T')[0] + ' 00:00:00'
        endDate = currentDate.toISOString().split('T')[0] + ' 23:59:59'
      } else if (type === 'last_month') {
        const lastMonth = new Date()
        lastMonth.setDate(currentDate.getDate() - 30)
        startDate = lastMonth.toISOString().split('T')[0] + ' 00:00:00'
        endDate = currentDate.toISOString().split('T')[0] + ' 23:59:59'
      } else if (type === 'current_month') {
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        startDate = firstDayOfMonth.toISOString().split('T')[0] + ' 00:00:00'
        endDate = currentDate.toISOString().split('T')[0] + ' 23:59:59'
      } else if (type === 'previous_month') {
        const firstDayOfPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
        const lastDayOfPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
        startDate = firstDayOfPreviousMonth.toISOString().split('T')[0] + ' 00:00:00'
        endDate = lastDayOfPreviousMonth.toISOString().split('T')[0] + ' 23:59:59'
      } else if (type === 'custom_range') {
        startDate = customStartDate ? customStartDate + ' 00:00:00' : ''
        endDate = customEndDate ? customEndDate + ' 23:59:59' : ''
      }
      const all = type === 'all'
      console.log('🚀 ~ exportToCSV ~ currentListStatus:', currentListStatus, startDate, endDate, all)

      let excelData
      try {
        excelData = await webApiService.getListTradeIn({
          email: userState.email,
          poolId: String(import.meta.env.VITE_COGNITO_POOL_ID),
          listStatus: currentListStatus,
          id_role: userState.id_role,
          seller: slug,
          page: 1,
          limit: 10,
          searchType: tableType,
          search: filterValueRef.current,
          downloadExcel: true,
          seal: sealRef.current,
          all,
          type,
          startDate,
          endDate,
          // Solo para Coppel ( primer seller en manejar regiones ) vamos a mandar la region y si viene, la tienda
          region: isCoppelMexico ? (regionRef.current || null) : null,
          selectedStore: isCoppelMexico ? (storeRef.current || null) : (userStoreRef.current || null)
        })
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: 'error' })
        setData({ ...data, loadingExcel: false })
        return
      }

      if (((type === 'all' || type === 'custom_range') && excelData.success === true) || (type && excelData.success === true && isSamsungMexico) || (type && excelData.success === true && (isHuaweiOnlineMexico || isHuaweiOnlinePeru)) || (type && excelData.success === true && isFalabellaChile)) {
        // descargar el excelData.data
        const link = document.createElement('a')
        link.href = excelData.data
        link.download = 'reporte_tradein_' + type + '.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setData({ ...data, loadingExcel: false })
        return
      }

      const rows = excelData.data.info.map((item: any) => {
        const row: any = {}
        columns.forEach((header: any) => {
          if (header.uid !== 'actions') {
            const isDate = typeof header.uid === 'string' && (header.uid.includes('fecha') || header.uid.includes('last_status_date'))
            if (header.uid === 'valor_residual') {
              if (item.final_grade === 'A') {
                row[header.name] = item.rv_a
              } else if (item.final_grade === 'B') {
                row[header.name] = item.rv_b
              } else {
                row[header.name] = ''
              }
            } else if (isDate && item[header.uid] !== null) {
              row[header.name] = UTCDateToCountryDate(item[header.uid])
            } else if (header.name === 'Nombre') {
              row[header.name] = item.name + ' ' + item.lastname
            } else {
              row[header.name] = item[header.uid]
            }
          }
        })
        return row
      })

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(rows)

      // Apply background color to 'status' column
      const statusColumnIndex = columns.findIndex((col: any) => col.uid === 'status') // +1 because Excel columns are 1-based
      if (statusColumnIndex > 0) {
        const range = XLSX.utils.decode_range(String(worksheet['!ref']))
        for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) { // Skip header row
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: statusColumnIndex })
          const cellValue = worksheet[cellAddress] ? worksheet[cellAddress].v : null
          if (!worksheet[cellAddress]) continue
          if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {}

          let bgColor = 'ffffff'
          bgColor = StatusColor(cellValue.toUpperCase())
          worksheet[cellAddress].s = {
            fill: { fgColor: { rgb: bgColor.replace('#', '') } },
            font: { color: { rgb: 'ffffff' } }
          }
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, excelName)
      const fileName = excelName + '_' + type + '.xlsx'
      XLSX.writeFile(workbook, fileName)
      setData({ ...data, loadingExcel: false })
    }

    console.log('🚀 ~ useTableDataDinamic ~ page:', page)

    return {
      selectedKeys,
      sortDescriptor,
      filterValue,
      sortedItems,
      bottomContent,
      onSearchChange,
      exportToCSV,
      onClear,
      setSortDescriptor,
      setPage,
      setSelectedKeys,
      loading: data.loading,
      loadingExcel: data.loadingExcel,
      onChangeTab,
      setStatusFilter,
      reload
    }
}

export default useTableDataDinamic
